// Quotes and Jobs service using repository pattern
import { Job, JobStatus, Quote } from '@/shared/types';
import { quoteRepository } from '@/shared/lib/repositories/QuoteRepository';
import { clientRepository } from '@/shared/lib/repositories/ClientRepository';
import { realDataService } from '@/shared/services/realDataService';

// Helper function to map quote status to job status
function mapQuoteStatusToJobStatus(status: string): JobStatus {
  switch (status) {
    case 'accepted':
      return JobStatus.COMPLETED;
    case 'rejected':
      return JobStatus.CANCELLED;
    case 'sent':
    case 'draft':
      return JobStatus.IN_PROGRESS;
    default:
      return JobStatus.PENDING;
  }
}

export const quoteService = {
  // Legacy Jobs method - now maps to Quotes
  getJobs: async (): Promise<Job[]> => {
    const profile = await realDataService.getProfile();
    const userId = profile.userId || '';

    // Convert quotes to jobs for backward compatibility
    const quotesResult = await quoteRepository.findAll(userId, {
      orderBy: 'created_at',
      ascending: false
    });

    if (!quotesResult.success) {
      throw new Error(quotesResult.error || 'Failed to fetch quotes');
    }

    const quotes = quotesResult.data.data || [];

    // Get clients for mapping
    const clientsResult = await clientRepository.findByUserId(userId);
    if (!clientsResult.success) {
      throw new Error(clientsResult.error || 'Failed to fetch clients');
    }
    const clients = clientsResult.data || [];

    return quotes.map((quote: Quote) => {
      const client = clients.find(c => c.id === quote.clientId);
      return {
        id: quote.id,
        userId: quote.userId || '',
        clientId: quote.clientId || '',
        title: quote.title,
        status: mapQuoteStatusToJobStatus(quote.status),
        totalAmount: Number(quote.totalAmount || 0),
        amountPaid: 0,
        createdAt: quote.createdAt,
        dueDate: quote.validUntil || '',
        services: quote.items?.map(item => ({
          id: item.id,
          serviceId: item.serviceId,
          name: item.name,
          rate: item.unitPrice || (item.rate * item.quantity),
          description: item.description
        })) || []
      } as Job;
    });
  },

  getQuotes: async (): Promise<Quote[]> => {
    const profile = await realDataService.getProfile();
    const userId = profile.userId || '';

    const result = await quoteRepository.findAll(userId, {
      orderBy: 'created_at',
      ascending: false
    });

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch quotes');
    }
    return result.data.data || [];
  },

  createQuote: async (quoteData: Partial<Quote>): Promise<Quote> => {
    const profile = await realDataService.getProfile();
    const userId = profile.userId || '';

    const { id, userId: _, ...data } = quoteData as Quote;
    const result = await quoteRepository.create(userId, data);
    if (!result.success) {
      throw new Error(result.error || 'Failed to create quote');
    }
    return result.data!;
  }
};
